import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import { DealRecordType } from '@marxbiotech/shared-types/bff';

import { accountLayoutQueryOptions } from '@lib/query/options/account';
import { mainLayoutQueryOptions } from '@lib/query/options/layout';
import { dealRecordsQueryOptions } from '@lib/query/options/payment';
import { getSettlementTypeBySetting } from '@lib/util/customer-settlement';
import { InlineStatusMessage } from '@modules/common/components/inline-status-message';
import { DealRecordTable } from '@modules/payment/components/deal-record-table';
import { MonthlySettlementContainer } from '@modules/payment/components/monthly-settlement-container';
import { MonthlySettlementSummary } from '@modules/payment/components/monthly-settlement-summary';
import { NormalSettlementSummary } from '@modules/payment/components/normal-settlement-summary';

// Deposit-related deal record types
const DEPOSIT_DEAL_RECORD_TYPES = [
  DealRecordType.DEPOSIT,
  DealRecordType.REWARD,
  DealRecordType.CHARGE,
  DealRecordType.REFUND,
  DealRecordType.REVERT,
  DealRecordType.ADJUST,
  DealRecordType.LOAN_PAYOUT,
  DealRecordType.LOAN_REPAYMENT,
];

/**
 * Account Payment Account Page (儲值金紀錄)
 *
 * Displays payment account balance and transaction history.
 * Monthly settlement customers see billing summary.
 * Normal settlement customers see deposit balance.
 */
export default function AccountPaymentAccountPage() {
  const { countryCode = 'tw' } = useParams<{ countryCode: string }>();
  const { data: accountData } = useSuspenseQuery(accountLayoutQueryOptions());
  const { data: layoutData } = useSuspenseQuery(mainLayoutQueryOptions(countryCode));

  const customer = accountData?.customer;
  const hasPaymentAccount = !!customer?.metadata?.marx_payment_account_id;
  const isMonthlySettlement =
    getSettlementTypeBySetting(layoutData.customer?.salesChannelSetting ?? null) === 'monthly';

  // Fetch deal records (only when user has a payment account)
  const { data: dealRecords = [], isError: isDealRecordsError } = useQuery({
    ...dealRecordsQueryOptions({
      types: DEPOSIT_DEAL_RECORD_TYPES,
      limit: 100,
      sortBy: 'dealtAt',
      orderBy: 'DESC',
    }),
    enabled: hasPaymentAccount,
  });

  if (!customer) {
    return null;
  }

  if (!hasPaymentAccount) {
    return (
      <div className="w-full" data-testid="marx-payment-account-page-wrapper">
        <div className="mb-8 flex items-center gap-y-4">
          <h1 className="text-black-30 text-3xl font-bold">儲值金紀錄</h1>
        </div>
        <InlineStatusMessage>尚未設定付款帳號</InlineStatusMessage>
      </div>
    );
  }

  return (
    <div className="w-full" data-testid="marx-payment-account-page-wrapper">
      <div className="mb-8 flex items-center gap-y-4">
        <h1 className="text-black-30 text-3xl font-bold">儲值金紀錄</h1>
        {isMonthlySettlement && <MonthlySettlementContainer />}
      </div>
      {isMonthlySettlement ? (
        <MonthlySettlementSummary />
      ) : (
        <NormalSettlementSummary />
      )}
      {isDealRecordsError ? (
        <InlineStatusMessage>無法取得交易紀錄，請稍後再試</InlineStatusMessage>
      ) : (
        <DealRecordTable dealRecords={dealRecords} />
      )}
    </div>
  );
}
